import { useEffect, useId, useMemo, useRef, useState, type ComponentProps } from 'react'
import { usePage } from '@inertiajs/react'
import { ChevronLeft, ChevronRight, Circle, Check, X, Minus } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'
import { TaskFilter } from '@/shared/ui/task-filter'
import { Separator } from '@/shared/ui/separator'
import { CommentCountIndicator } from '@/shared/ui/comment-count-indicator'
import { QuickSelectDatePickerInput } from '@/shared/ui/quick-select-date-picker-input'
import { t } from '@/shared/lib/i18n'
import { cn } from '@/shared/lib/utils'
import { isReportRangeOrderInvalid } from '@/shared/lib/report-range-order'
import type { WebInstrumentCalendarView } from '@/shared/types/web'
import type { SharedProps } from '@/shared/types/SharedProps'
import type WebInstrumentsTaskLogDataField from '@/shared/types/web/WebInstrumentsTaskLogDataField'
import { CalendarGrid, type ExtraRowType } from './CalendarGrid'
import { useTaskFrequencyDetailsPreference } from './useTaskFrequencyDetailsPreference'
import { hasNonTaskCalendarContent } from './hasNonTaskCalendarContent'
import { isLogClickableCell, isMissedClickableCell } from './calendarCellClickability'
import {
  countHiddenSuccessfulEntries,
  isAlertCalendarCell,
  isListedCalendarCell,
} from './calendarListAlerts'
import LogDetailsDialog from './LogDetailsDialog'
import InstrumentServiceReportListDialog from './InstrumentServiceReportListDialog'
import InstrumentStatusChangesDialog from './InstrumentStatusChangesDialog'
import InstrumentDepartmentMovesDialog from './InstrumentDepartmentMovesDialog'
import InstrumentFailuresDialog from './InstrumentFailuresDialog'
import TaskDayOccurrencesDialog, {
  type TaskDayOccurrencesModalState,
} from './TaskDayOccurrencesDialog'
import TaskChangeLogDialog from './TaskChangeLogDialog'
import {
  failureRowModalStateAfterDelete,
  runCalendarLogDeleteRefresh,
  type FailureRowModalState,
} from './calendarLogDeleteRefresh'
import {
  formatMonthYearLabel,
  shiftMonth,
  endOfMonth,
  currentMonthStart,
} from '@/shared/lib/tenantDateTime'
import { getCellSymbol } from '@/shared/lib/calendar-cell-symbol'
import { CONFIDENTIAL_MASK } from '@/shared/lib/confidential-mask'
import type { CellState, SymbolKind } from '@/shared/lib/calendar-cell-symbol'
import { QA_ALERT_CATEGORY_MAP } from '@/shared/constants/qaAlertConfig'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

// Re-export for callers that historically imported refresh helpers from this panel.
export {
  reloadInstrumentShowPropsAfterCalendarDelete,
  runCalendarLogDeleteRefresh,
} from './calendarLogDeleteRefresh'

export interface InstrumentCalendarViewPanelProps {
  instrumentId: number
  instrumentLabel: string
  serialNumber: string | null
  initialCalendarData: WebInstrumentCalendarView | null
  /**
   * Site-local "today" (YYYY-MM-DD) resolved server-side. Always present so the initial
   * month default is site-local even before the calendar payload has loaded.
   */
  siteToday?: string
  /** Site IANA zone for recorded-log abbreviations inside this panel's dialogs. */
  siteTimeZone?: string | null
  /**
   * Called when the panel needs fresh calendar data (month navigation, task filter, etc.).
   * Returns the new calendar payload or null on error.
   */
  fetchCalendarData: (
    from: string,
    to: string,
    taskIds: number[],
  ) => Promise<WebInstrumentCalendarView | null>
  /**
   * When false, defers the initial fetch until true.
   * Used by the Reports tab to lazy-load only on first activation.
   * Defaults to true (load immediately if no initial data).
   */
  isActive?: boolean
  /**
   * When true, the sign-off history block at the bottom is hidden.
   * Used on the monthly report page where a dedicated Signatures section
   * already shows the same audit trail.
   */
  hideSignOffHistory?: boolean
  /**
   * Called after a successful compliance review action (NT/CNM/ODE/Fix Log) or a
   * task-log void/delete, in addition to the internal calendar refresh. Use this to
   * trigger reloads of other page-level Inertia props (e.g. activityFeed, summary, or
   * the Tasks tab's currentSchedule) from the parent page without coupling the panel
   * to those props.
   */
  onAfterReview?: () => void
  /**
   * Called whenever the panel fetches and receives new calendar data
   * (month navigation, task filter apply, or post-review refresh).
   * Allows a parent page to keep a local copy of the current calendar state
   * for derived computations (e.g. KPI counts) without lifting all state out.
   */
  onCalendarDataChange?: (data: WebInstrumentCalendarView) => void
  /**
   * When false, the alert-only toggle does NOT affect the CalendarGrid —
   * the grid always renders the full month view. The toggle still fires
   * `onAlertOnlyChange` so the parent page can filter its own list content.
   * Defaults to true (existing behavior: toggle affects the calendar).
   */
  alertOnlyAffectsCalendar?: boolean
  /**
   * Called whenever the alert-only toggle changes state.
   * Allows a parent page to react to toggle changes without owning the toggle.
   */
  onAlertOnlyChange?: (alertOnly: boolean) => void
  /**
   * When provided, locks the calendar to this specific month (ISO month string, e.g. "2026-06").
   * Prev/next navigation arrows are hidden and month navigation is disabled.
   * Used in the Monthly Report context to prevent browsing outside the report period.
   */
  lockedToMonth?: string | null
  /**
   * Optional page-level Task filter ids (e.g. Maintenance Logs `cal_task_ids`).
   * When provided, keeps the panel's task multi-select aligned with that filter —
   * including clearing to [] when the page filter is cleared — without resetting
   * local calendar task selection on unrelated `initialCalendarData` refreshes.
   */
  pageLevelTaskIds?: number[]
  /**
   * When true, hides the panel's own Task filter (AC5 — Maintenance Logs Report
   * renders the same TaskFilter at page level, synced down via `pageLevelTaskIds`;
   * showing both let them diverge since edits to the panel's own filter were
   * never synced back up). Defaults to false —
   * Instruments/Show's Reports tab has no page-level task filter.
   */
  hideTaskFilter?: boolean
  /**
   * Bump this value (e.g. an incrementing counter) to force the panel to silently
   * re-fetch the currently-viewed month with its current task filter. For actions that
   * change compliance data for this instrument from OUTSIDE the panel's own dialogs —
   * e.g. a Non-Compliance review/resolve on the Dashboard/Tasks page (REBUILD-409) —
   * there is no dialog-close hook to hang a refresh off of, unlike onAfterReview (which
   * only covers the panel's own Log Details / day-occurrences dialogs). Ignored on the
   * initial render and whenever the value is unchanged, so passing a stable constant is
   * a no-op.
   */
  refreshSignal?: number
  /**
   * When true, hides the alert-only toggle and the flat task-log list view below
   * the calendar grid. Used by Maintenance Logs Report which owns a unified
   * activity feed (MaintenanceLogsActivityFeed) that replaces this panel's list.
   */
  hideListView?: boolean
  /**
   * When false, preserves the existing always-visible frequency details without
   * rendering the shared preference control. Used by the Monthly Report, which is
   * outside the task-frequency-details preference scope.
   */
  showTaskFrequencyDetailsControl?: boolean
  /**
   * Called when the user applies a new From/To date range via the panel's own pickers
   * (auto-apply on valid change). Use this on pages where other sections (log entries,
   * KPI counts, activity feed) must also re-fetch for the new range — e.g. Maintenance
   * Logs Report. When provided, the auto-apply does NOT call `runFetch` internally;
   * the page navigation is expected to reload the panel with fresh `initialCalendarData`.
   * When absent (Instruments/Show Reports tab), auto-apply calls `runFetch` directly.
   */
  onRangeApplied?: (from: string, to: string) => void
  /**
   * Instrument Reports only (REBUILD-602 / JW 582): Task filter uses search, a Show All–style
   * Select All checkbox (toggle clears visible ids), and an explicit Apply button. Draft
   * edits do not refetch until Apply. Dashboard, Maintenance Logs, and report detail leave
   * this false (apply on each selection / Show All).
   */
  explicitTaskFilterApply?: boolean
}

interface LogModalState {
  taskLogId: number
  instrumentTaskId: number | null
  date: string
  unscheduledOnly?: boolean
}

type TaskRow = WebInstrumentCalendarView['taskRows'][number]

const hasSameTaskIds = (first: number[], second: number[]) =>
  first.length === second.length && first.every((taskId) => second.includes(taskId))

const InstrumentCalendarViewPanel = ({
  instrumentId,
  instrumentLabel,
  serialNumber,
  initialCalendarData,
  siteToday: siteTodayProp,
  siteTimeZone,
  fetchCalendarData,
  isActive = true,
  hideSignOffHistory = false,
  onAfterReview,
  onCalendarDataChange,
  alertOnlyAffectsCalendar: _alertOnlyAffectsCalendar = true,
  onAlertOnlyChange,
  lockedToMonth = null,
  pageLevelTaskIds,
  hideTaskFilter = false,
  hideListView = false,
  showTaskFrequencyDetailsControl = true,
  refreshSignal,
  onRangeApplied,
  explicitTaskFilterApply = false,
}: InstrumentCalendarViewPanelProps) => {
  const { currentTenantId, currentUserInfo } = usePage<SharedProps>().props
  const [calendarData, setCalendarData] = useState<WebInstrumentCalendarView | null>(
    initialCalendarData,
  )
  // For lazy-load (initialCalendarData=null), fall back to siteTodayProp so the
  // initial render never has empty bounds — prevents a blank initial render before
  // the isActive effect fires and sets real data.
  const lazyDefaultFrom = siteTodayProp ? currentMonthStart(siteTodayProp) : ''
  const lazyDefaultTo = lazyDefaultFrom ? endOfMonth(lazyDefaultFrom) : ''
  const [currentFrom, setCurrentFrom] = useState(
    initialCalendarData?.defaultFilters.fromDate ?? lazyDefaultFrom,
  )
  // From/To range bounds — drive canGoPrev/canGoNext and range-clamped fetch bounds.
  const [filterFrom, setFilterFrom] = useState(
    initialCalendarData?.defaultFilters.fromDate ?? lazyDefaultFrom,
  )
  const [filterTo, setFilterTo] = useState(
    initialCalendarData?.defaultFilters.toDate ?? lazyDefaultTo,
  )
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>(
    initialCalendarData?.defaultFilters.taskIds ?? [],
  )
  // Reports explicit Apply draft — kept in sync via TaskFilter onDraftChange. Survives
  // popover close so Apply works with the list shut; discarded when the list reopens (AC7).
  const [draftTaskIds, setDraftTaskIds] = useState<number[]>(
    initialCalendarData?.defaultFilters.taskIds ?? [],
  )
  const [loading, setLoading] = useState(false)
  const initialLoadTriggered = useRef(false)
  // A slower calendar response must not replace data from a newer selection.
  const calendarFetchGenerationRef = useRef(0)
  // Skip the first run of the filterFrom/filterTo auto-apply effect (initial mount).
  const filterRangeAppliedRef = useRef(false)
  // Read onRangeApplied through a ref so the effect doesn't depend on it directly.
  const onRangeAppliedRef = useRef(onRangeApplied)
  useEffect(() => {
    onRangeAppliedRef.current = onRangeApplied
  })

  // Every mount starts alerts-only (legacy `showAlertsOnly = true`, REBUILD-603). The choice
  // is kept only while this panel stays mounted; it is not stored or written to the URL.
  const [alertOnly, setAlertOnly] = useState(true)
  const [showTaskFrequencyDetails, setShowTaskFrequencyDetails] = useTaskFrequencyDetailsPreference(
    currentTenantId,
    currentUserInfo?.id,
  )
  // Dashboard can render several panels at once, so the switch id must be unique per panel.
  const taskFrequencyDetailsToggleId = useId()

  const [logModalState, setLogModalState] = useState<LogModalState | null>(null)
  const [dayOccurrencesModalState, setDayOccurrencesModalState] =
    useState<TaskDayOccurrencesModalState | null>(null)

  const [serviceReportDate, setServiceReportDate] = useState<string | null>(null)
  const [statusChangesDate, setStatusChangesDate] = useState<string | null>(null)
  const [departmentMovesDate, setDepartmentMovesDate] = useState<string | null>(null)
  const [failureRowModalState, setFailureRowModalState] = useState<FailureRowModalState | null>(
    null,
  )
  const [changeLogTaskRow, setChangeLogTaskRow] = useState<TaskRow | null>(null)

  // Prefer the always-present prop (resolved server-side, available before the calendar
  // payload loads); fall back to the payload value for callers that don't pass the prop.
  const siteToday = siteTodayProp ?? calendarData?.siteToday

  useEffect(() => {
    if (!isActive || calendarData !== null || initialLoadTriggered.current) return
    initialLoadTriggered.current = true
    const from = currentMonthStart(siteToday)
    setCurrentFrom(from)
    runFetch(from, endOfMonth(from), [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive])

  useEffect(() => {
    if (initialCalendarData === null) return
    // Guard: when a review action fires router.post(preserveState: true), the
    // redirect-back causes Inertia to re-render with the server's default-month
    // calendar (which may differ from the month the user navigated to). Skip the
    // sync in that case so the user's navigation state is not overwritten;
    // the post-review runFetch call will supply the correct month's data.
    const incomingMonth = initialCalendarData.defaultFilters.fromDate
    if (currentFrom !== '' && incomingMonth !== currentFrom) return
    setCalendarData(initialCalendarData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCalendarData])

  // Sync only from an explicit page-level Task filter prop (Maintenance Logs).
  // Do not derive this from initialCalendarData.defaultFilters — that would wipe
  // local multi-select after unrelated calendar prop refreshes on other pages.
  // Depend on a stable serialized key so a new [] / [id] array reference each render
  // cannot reset the user's local calendar task multi-select.
  const pageLevelTaskIdsKey =
    pageLevelTaskIds === undefined ? undefined : pageLevelTaskIds.join(',')
  useEffect(() => {
    if (pageLevelTaskIds === undefined) return
    setSelectedTaskIds(pageLevelTaskIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync on value identity, not array ref
  }, [pageLevelTaskIdsKey])

  // Range-clamped fetch bounds (AC3): first month of range uses filterFrom as start,
  // last month uses filterTo as end; middle months get full-month bounds. Used ONLY for
  // same-month re-fetches that must honour an explicit From/To filter (post-review refresh,
  // calendar-delete refresh). Month-pager navigation does NOT clamp — see handlePreviousMonth /
  // handleNextMonth, which always load a full calendar month (REBUILD-472).
  const clampedFrom = (monthStart: string): string =>
    monthStart.slice(0, 7) === filterFrom.slice(0, 7) ? filterFrom : monthStart

  const clampedTo = (monthStart: string): string =>
    monthStart.slice(0, 7) === filterTo.slice(0, 7) ? filterTo : endOfMonth(monthStart)

  // REBUILD-472: the Prev/Next arrows navigate full calendar months and MUST NOT be gated on
  // the From/To filter range. The previous implementation disabled both arrows whenever the
  // viewed month sat at (or outside) the From/To boundary — which, in the default single-month
  // range (From=1st, To=last of the same month), left both arrows permanently inert on every
  // surface (UAT 440). Prev is always available (any past month is valid); Next is available up
  // to and including the current site-local month, then capped (legacy "next limited for future
  // months"). Only `loading` can temporarily disable them, so navigation never leaves them stuck.
  // End-before-start is rejected in words (same copy as pages/Reports/*); the auto-apply
  // effect above keeps the previous grid untouched until the range is valid again.
  const dateRangeInvalid = isReportRangeOrderInvalid(filterFrom, filterTo)
  const currentSiteMonth = currentMonthStart(siteToday).slice(0, 7)
  const canGoPrev = !loading
  const canGoNext = !loading && currentFrom.slice(0, 7) < currentSiteMonth

  const runFetch = (from: string, to: string, taskIds: number[], onComplete?: () => void) => {
    const fetchGeneration = calendarFetchGenerationRef.current + 1
    calendarFetchGenerationRef.current = fetchGeneration
    const isCurrentFetch = () => calendarFetchGenerationRef.current === fetchGeneration

    setLoading(true)
    fetchCalendarData(from, to, taskIds)
      .then((data) => {
        if (!isCurrentFetch()) return
        if (data === null) return
        setCalendarData(data)
        onCalendarDataChange?.(data)
        // Only after cells actually update — null (e.g. Inertia 409 reload) must not advance
        // Failures remaining-ids ahead of a stale failure_row (AC4).
        onComplete?.()
      })
      .catch((error: unknown) => {
        if (!isCurrentFetch()) return
        console.error('Failed to fetch calendar data', error)
      })
      .finally(() => {
        if (!isCurrentFetch()) return
        setLoading(false)
      })
  }

  // Skips the initial render (ref starts equal to the incoming prop) so mounting with a
  // non-zero refreshSignal never double-fetches alongside the isActive initial-load effect.
  const previousRefreshSignalRef = useRef(refreshSignal)
  useEffect(() => {
    if (refreshSignal === undefined || refreshSignal === previousRefreshSignalRef.current) return
    previousRefreshSignalRef.current = refreshSignal
    const from = currentFrom || currentMonthStart(siteToday)
    runFetch(clampedFrom(from), clampedTo(from), selectedTaskIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal])

  // Auto-apply: re-fetch (or delegate to onRangeApplied) when From/To range changes.
  // Skips the initial render so mounting with existing data never double-fetches.
  useEffect(() => {
    if (!filterRangeAppliedRef.current) {
      filterRangeAppliedRef.current = true
      return
    }
    if (!filterFrom || !filterTo || isReportRangeOrderInvalid(filterFrom, filterTo)) return
    if (onRangeAppliedRef.current) {
      // Let the parent page handle navigation (e.g. Maintenance Logs full router.get).
      onRangeAppliedRef.current(filterFrom, filterTo)
      return
    }
    const monthStart = currentMonthStart(filterFrom)
    setCurrentFrom(monthStart)
    runFetch(clampedFrom(monthStart), clampedTo(monthStart), selectedTaskIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterFrom, filterTo])

  // Calendar cells via local fetch (full month bounds); Show header + Tasks schedule
  // via runCalendarLogDeleteRefresh. Optional onCalendarRefreshSuccess runs only after runFetch
  // applies non-null data (not on catch / null) — Failures remaining-ids must wait so modal
  // state cannot race ahead of cell refresh (AC4). Guard typeof: LogDetails finishVoid passes
  // deletedLogId as the sole onAfterDelete arg — never treat that number as this callback.
  const refreshAfterLogDelete = (onCalendarRefreshSuccess?: unknown) => {
    const onSuccess =
      typeof onCalendarRefreshSuccess === 'function'
        ? (onCalendarRefreshSuccess as () => void)
        : undefined
    const monthStart = currentFrom || currentMonthStart(siteToday)
    runCalendarLogDeleteRefresh(() =>
      runFetch(clampedFrom(monthStart), clampedTo(monthStart), selectedTaskIds, onSuccess),
    )
  }

  // Month-pager navigation loads a FULL calendar month (1st → last), independent of the
  // From/To filter range (REBUILD-472). Clicking an arrow always moves exactly one calendar
  // month and reloads data for the whole of it — Feb/30/31-day months land on the correct last
  // day via endOfMonth. Keeping this off clampedFrom/clampedTo is what lets the arrows work in
  // the default single-month state, where the old range-clamp disabled them entirely.
  const navigateToMonth = (newMonthStart: string) => {
    setCurrentFrom(newMonthStart)
    runFetch(newMonthStart, endOfMonth(newMonthStart), selectedTaskIds)
  }

  const handlePreviousMonth = () => {
    navigateToMonth(shiftMonth(currentFrom || currentMonthStart(siteToday), -1))
  }

  const handleNextMonth = () => {
    navigateToMonth(shiftMonth(currentFrom || currentMonthStart(siteToday), 1))
  }

  // REBUILD-528: applying a task selection must re-fetch the calendar scoped to it. Previously
  // this only updated local state, so `reports_task_ids` was never sent and the filter appeared
  // to do nothing. Store the selection and fetch the currently-viewed month with it (clearing
  // to [] re-fetches all tasks). When the panel is driven by a page-level task filter
  // (Maintenance Logs), that parent owns the fetch, so skip the local re-fetch here to avoid a
  // double load.
  const applyTaskIds = (nextTaskIds: number[]) => {
    setSelectedTaskIds(nextTaskIds)
    setDraftTaskIds(nextTaskIds)

    if (pageLevelTaskIds !== undefined) return
    const monthStart = currentFrom || currentMonthStart(siteToday)
    runFetch(monthStart, endOfMonth(monthStart), nextTaskIds)
  }

  const isTaskFilterApplyDisabled = loading || hasSameTaskIds(draftTaskIds, selectedTaskIds)

  const handleLogCellClick = ({
    instrumentTaskId,
    taskLogId,
    taskName,
    taskNotes,
    date,
    intervalTypeDescription,
    dataFields,
    cellState,
  }: {
    instrumentTaskId: number | null
    taskLogId: number
    taskName: string
    taskNotes: string | null
    date: string
    intervalTypeDescription: string | null
    dataFields: WebInstrumentsTaskLogDataField[]
    cellState: CellState
  }) => {
    // CalendarGrid only calls this for cells with instrumentTaskLogId (success/NT, or
    // executed QC-fail — RC-linked or log-only). True miss cells (requiredComplianceId,
    // no log id) go through handleMissedCellClick → day-occurrences so miss review stays
    // reachable when a same-day orphan QC-fail exists (AC5).
    // Executed QC-fail: Log Details (day-occurrences drops qc_failed orphans).
    // Log Details loads task identity from the log payload — no local taskName needed.
    if (cellState === 'failure') {
      setLogModalState({
        instrumentTaskId,
        taskLogId,
        date,
      })
      return
    }

    // Success / NT: unified day-occurrence list (logs + misses) with date strip.
    setDayOccurrencesModalState({
      instrumentTaskId,
      taskName,
      taskNotes,
      date,
      intervalTypeDescription,
      dataFields,
    })
  }

  // True miss / ODE / CNM: prefer day-occurrences whenever requiredComplianceId is present
  // (CalendarGrid gates this path). Do not open Log Details for orphan QC-fail on the same day.
  const handleMissedCellClick = ({
    instrumentTaskId,
    taskName,
    taskNotes,
    date,
    intervalTypeDescription,
    dataFields,
  }: {
    instrumentTaskId: number
    requiredComplianceId: number
    taskName: string
    taskNotes: string | null
    date: string
    intervalTypeDescription: string | null
    cellState: 'failure' | 'override' | 'confirmed'
    dataFields: WebInstrumentsTaskLogDataField[]
  }) => {
    setDayOccurrencesModalState({
      instrumentTaskId,
      taskName,
      taskNotes,
      date,
      intervalTypeDescription,
      dataFields,
    })
  }

  const handleLogModalClose = () => setLogModalState(null)
  const handleDayOccurrencesModalClose = () => setDayOccurrencesModalState(null)

  const handleExtraRowCellClick = ({ rowType, date }: { rowType: ExtraRowType; date: string }) => {
    if (rowType === 'serviceReports') setServiceReportDate(date)
    else if (rowType === 'statusChanges') setStatusChangesDate(date)
    else if (rowType === 'departmentMoves') setDepartmentMovesDate(date)
  }

  const handleFailureRowCellClick = ({
    date,
    instrumentTaskLogIds,
    failures,
  }: {
    date: string
    instrumentTaskLogIds: number[]
    failures: Array<{ id: number; completedAtLocal: string | null }>
  }) => {
    setFailureRowModalState({ date, instrumentTaskLogIds, failures })
  }

  const handleUnscheduledCellClick = ({ taskLogId, date }: { taskLogId: number; date: string }) => {
    setLogModalState({
      instrumentTaskId: null,
      taskLogId,
      date,
      unscheduledOnly: true,
    })
  }

  const handleAlertOnlyChange = (checked: boolean) => {
    setAlertOnly(checked)
    onAlertOnlyChange?.(checked)
  }

  const isCalendarGridRendered = calendarData !== null || loading
  const isTaskFrequencyDetailsVisible = !showTaskFrequencyDetailsControl || showTaskFrequencyDetails

  const successCount = countHiddenSuccessfulEntries(calendarData?.taskRows ?? [])

  const tasksAvailable = calendarData?.tasksAvailable ?? []

  const signOffHistory = calendarData?.signOffHistory ?? []

  const dateToLogIdMap = useMemo((): Record<string, number> => {
    if (!calendarData) return {}
    const result: Record<string, number> = {}

    for (const taskRow of calendarData.taskRows) {
      for (const cell of taskRow.cells) {
        if (cell.state === 'pending') continue
        if (cell.date in result) continue

        if (
          (cell.state === 'success' || cell.state === 'no_testing' || cell.state === 'failure') &&
          cell.meta.instrumentTaskLogId != null
        ) {
          result[cell.date] = cell.meta.instrumentTaskLogId
        } else if (
          (cell.state === 'failure' || cell.state === 'override' || cell.state === 'confirmed') &&
          cell.meta.requiredComplianceId != null
        ) {
          result[cell.date] = 0
        }
      }
    }

    for (const cell of calendarData.unscheduledRow.cells) {
      if ('firstLogId' in cell && cell.firstLogId != null && !(cell.date in result)) {
        result[cell.date] = cell.firstLogId as number
      }
    }

    return result
  }, [calendarData])

  // Day-occurrences fetch is task-scoped; strip must match that task so dates without
  // this task's content are not implied (instrument-wide map would show empty days).
  const dayOccurrencesDateToLogIdMap = useMemo((): Record<string, number> => {
    if (!calendarData || !dayOccurrencesModalState) return {}
    const scopedTaskId = dayOccurrencesModalState.instrumentTaskId
    if (scopedTaskId == null) return dateToLogIdMap

    const result: Record<string, number> = {}
    const taskRow = calendarData.taskRows.find((row) => row.instrumentTaskId === scopedTaskId)
    if (!taskRow) return result

    for (const cell of taskRow.cells) {
      if (cell.state === 'pending') continue
      if (
        (cell.state === 'success' || cell.state === 'no_testing') &&
        cell.meta.instrumentTaskLogId != null
      ) {
        result[cell.date] = cell.meta.instrumentTaskLogId
      } else if (
        (cell.state === 'failure' || cell.state === 'override' || cell.state === 'confirmed') &&
        cell.meta.requiredComplianceId != null &&
        cell.meta.instrumentTaskLogId == null
      ) {
        // True miss / review — strip entry (executed QC-fail uses Log Details, not this dialog).
        result[cell.date] = 0
      }
    }

    return result
  }, [calendarData, dayOccurrencesModalState, dateToLogIdMap])

  const calendarToDate = currentFrom ? endOfMonth(currentFrom) : null

  return (
    <div className="space-y-4">
      {/* Toolbar: date range pickers + month navigation + task filter */}
      <div className="flex flex-wrap items-center gap-3">
        {lockedToMonth ? (
          <span className="text-sm font-semibold">
            {currentFrom ? formatMonthYearLabel(currentFrom) : ''}
          </span>
        ) : (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handlePreviousMonth}
              disabled={!canGoPrev}
              aria-label={t('instruments.show.reports.pager.previous')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-muted-foreground">
                {t('instruments.show.reports.filters.from')}
              </Label>
              <QuickSelectDatePickerInput
                value={filterFrom}
                onChange={setFilterFrom}
                className="h-8 w-32 text-xs"
              />
            </div>

            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-muted-foreground">
                {t('instruments.show.reports.filters.to')}
              </Label>
              <QuickSelectDatePickerInput
                value={filterTo}
                onChange={setFilterTo}
                className="h-8 w-32 text-xs"
              />
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleNextMonth}
              disabled={!canGoNext}
              aria-label={t('instruments.show.reports.pager.next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            {dateRangeInvalid && (
              <p role="alert" className="w-full basis-full min-w-0 text-sm text-destructive">
                {t('reports.validation.endBeforeStart')}
              </p>
            )}

            {/* Month-year label for the currently viewed month — pushed onto its own
                row by w-full so it appears between the pickers toolbar and the grid,
                matching the visual position on the Maintenance Logs Report (AC1). */}
            <span className="w-full text-sm font-semibold">
              {currentFrom ? formatMonthYearLabel(currentFrom) : ''}
            </span>
          </>
        )}

        {tasksAvailable.length > 0 && !hideTaskFilter && (
          <>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">
                {t('instruments.show.reports.filters.tasks')}
              </Label>
              <TaskFilter
                options={tasksAvailable}
                selectedIds={selectedTaskIds}
                onApply={applyTaskIds}
                onDraftChange={explicitTaskFilterApply ? setDraftTaskIds : undefined}
                commitMode={explicitTaskFilterApply ? 'explicit' : 'on-select'}
                massActions={explicitTaskFilterApply ? 'select-deselect' : 'show-all'}
                enableSearch={explicitTaskFilterApply}
              />
              {explicitTaskFilterApply && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isTaskFilterApplyDisabled}
                  // Commit on pointerdown (and preventDefault) so an outside-Apply gesture
                  // applies the draft before the popover dismiss resets it (AC6/AC7).
                  onPointerDown={(event) => {
                    event.preventDefault()
                    if (isTaskFilterApplyDisabled) return
                    applyTaskIds(draftTaskIds)
                  }}
                  onClick={(event) => {
                    // Keyboard Space/Enter synthesizes click with detail 0; mouse already
                    // committed on pointerdown.
                    if (event.detail !== 0 || isTaskFilterApplyDisabled) return
                    applyTaskIds(draftTaskIds)
                  }}
                >
                  {t('instruments.show.reports.filters.apply')}
                </Button>
              )}
            </div>
          </>
        )}

        {/* Hidden below `sm`: the mobile accordion never renders frequency details. */}
        {showTaskFrequencyDetailsControl && isCalendarGridRendered && (
          <div className="hidden items-center gap-2 text-sm sm:flex">
            <Switch
              id={taskFrequencyDetailsToggleId}
              checked={showTaskFrequencyDetails}
              onCheckedChange={setShowTaskFrequencyDetails}
            />
            <Label htmlFor={taskFrequencyDetailsToggleId} className="cursor-pointer">
              {t('instruments.show.reports.showTaskFrequencyDetails')}
            </Label>
          </div>
        )}
      </div>

      {/* Calendar grid — unaffected by alert-only toggle (AC #5) */}
      {isCalendarGridRendered && (
        <CalendarGrid
          reportsCalendar={calendarData}
          loading={loading}
          alertOnly={false}
          showTaskFrequencyDetails={isTaskFrequencyDetailsVisible}
          onLogCellClick={handleLogCellClick}
          onMissedCellClick={handleMissedCellClick}
          onExtraRowCellClick={handleExtraRowCellClick}
          onUnscheduledCellClick={handleUnscheduledCellClick}
          onFailureRowCellClick={handleFailureRowCellClick}
          onTaskNameClick={setChangeLogTaskRow}
        />
      )}

      {/* Legend + Alert-only toggle — toggle controls list view only */}
      {calendarData !== null && (
        <>
          <CalendarLegend calendarData={calendarData} />
          <SymbolLegend />
          {!hideListView && (
            <>
              <AlertOnlyToggle
                alertOnly={alertOnly}
                successCount={successCount}
                onChange={handleAlertOnlyChange}
              />
              <CalendarListView
                calendarData={calendarData}
                alertOnly={alertOnly}
                onLogCellClick={handleLogCellClick}
                onMissedCellClick={handleMissedCellClick}
              />
            </>
          )}
        </>
      )}

      {/* Sign-off History */}
      {!hideSignOffHistory && calendarData !== null && (
        <SignOffHistoryBlock signOffHistory={signOffHistory} loading={loading} />
      )}

      {/* Log Details / NT modal */}
      <LogDetailsDialog
        instrumentId={instrumentId}
        instrumentTaskId={logModalState?.instrumentTaskId ?? null}
        instrumentLabel={instrumentLabel}
        serialNumber={serialNumber}
        taskLogId={logModalState?.taskLogId ?? null}
        date={logModalState?.date ?? null}
        fromDate={currentFrom || null}
        toDate={calendarToDate}
        dateToLogIdMap={logModalState?.unscheduledOnly ? undefined : dateToLogIdMap}
        unscheduledOnly={logModalState?.unscheduledOnly ?? false}
        onClose={handleLogModalClose}
        // finishVoid passes deletedLogId; ignore it — refresh has no remaining-ids step here.
        onAfterDelete={() => refreshAfterLogDelete(onAfterReview)}
      />

      {/* Unified day-occurrence dialog: logs + misses in one list, with task-scoped date strip */}
      <TaskDayOccurrencesDialog
        instrumentId={instrumentId}
        instrumentLabel={instrumentLabel}
        serialNumber={serialNumber}
        modalState={dayOccurrencesModalState}
        onClose={handleDayOccurrencesModalClose}
        dateToLogIdMap={dayOccurrencesDateToLogIdMap}
        placementFromDate={currentFrom || null}
        placementToDate={calendarToDate}
        onReviewSuccess={() => {
          // Calendar cells via local fetch (range-clamped bounds); Show header + Tasks schedule
          // via runCalendarLogDeleteRefresh. Do not use a full router.reload() — that remounts
          // this panel and tears down the open day-occurrences dialog (E45-S1 AC5). Callers
          // that need extra page props (e.g. Maintenance Logs activity feed) pass onAfterReview.
          const monthStart = currentFrom || currentMonthStart(siteToday)
          runCalendarLogDeleteRefresh(() =>
            runFetch(
              clampedFrom(monthStart),
              clampedTo(monthStart),
              selectedTaskIds,
              onAfterReview,
            ),
          )
        }}
      />

      {/* Extra-row drill-down dialogs */}
      <InstrumentServiceReportListDialog
        instrumentId={instrumentId}
        date={serviceReportDate}
        onClose={() => setServiceReportDate(null)}
      />
      <InstrumentStatusChangesDialog
        instrumentId={instrumentId}
        date={statusChangesDate}
        onClose={() => setStatusChangesDate(null)}
      />
      <InstrumentDepartmentMovesDialog
        instrumentId={instrumentId}
        date={departmentMovesDate}
        onClose={() => setDepartmentMovesDate(null)}
      />

      {/* Failure(s) dialog — key remounts on close so multi-failure drill-down state resets */}
      <InstrumentFailuresDialog
        key={failureRowModalState?.date ?? 'closed'}
        instrumentId={instrumentId}
        instrumentLabel={instrumentLabel}
        serialNumber={serialNumber}
        date={failureRowModalState?.date ?? null}
        instrumentTaskLogIds={failureRowModalState?.instrumentTaskLogIds ?? []}
        failures={failureRowModalState?.failures ?? []}
        fromDate={currentFrom || null}
        toDate={calendarToDate}
        dateToLogIdMap={dateToLogIdMap}
        onClose={() => setFailureRowModalState(null)}
        onAfterDelete={(deletedLogId) => {
          // E45-S1 AC4/AC5 + E45-S3: refresh Failures cells + Show header + Tasks schedule via
          // runCalendarLogDeleteRefresh (no Reports poll). Chain remaining-ids / close onto
          // runFetch success so a failed refresh cannot leave the modal ahead of a stale
          // Failures cell count (REBUILD-374 AC4 race).
          refreshAfterLogDelete(() => {
            setFailureRowModalState((previous) =>
              failureRowModalStateAfterDelete(previous, deletedLogId),
            )
          })
        }}
      />

      {/* Task Change Log modal */}
      <TaskChangeLogDialog
        instrumentId={instrumentId}
        instrumentLabel={instrumentLabel}
        serialNumber={serialNumber}
        taskName={changeLogTaskRow?.taskName ?? null}
        taskVersions={changeLogTaskRow?.taskVersions ?? []}
        siteTimeZone={siteTimeZone}
        onClose={() => setChangeLogTaskRow(null)}
      />
    </div>
  )
}

// ── AlertOnlyToggle ───────────────────────────────────────────────────────────

interface AlertOnlyToggleProps {
  alertOnly: boolean
  successCount: number
  onChange: (checked: boolean) => void
}

const AlertOnlyToggle = ({ alertOnly, successCount, onChange }: AlertOnlyToggleProps) => {
  const alertOnlyToggleId = useId()
  // Label describes the CURRENT mode (legacy calendarView.template.html:284-287), same as the
  // instrument History toggle: on → "Showing Alert only (N Successful Events Hidden)";
  // off → "Showing Full Details".
  const label = alertOnly
    ? `${t('instruments.show.history.showAlertsOnly')} ${t('instruments.show.history.successfulEventsHidden', { count: successCount })}`
    : t('instruments.show.history.showFullDetails')

  return (
    <div className="flex items-center gap-2 text-sm">
      <Switch id={alertOnlyToggleId} checked={alertOnly} onCheckedChange={onChange} />
      <Label htmlFor={alertOnlyToggleId} className="cursor-pointer">
        {label}
      </Label>
    </div>
  )
}

// ── CalendarLegend ────────────────────────────────────────────────────────────

interface CalendarLegendProps {
  calendarData: WebInstrumentCalendarView
}

interface LegendItemDef {
  key: string
  label: string
  dotClass: string
  hasEvents: boolean
}

export const CalendarLegend = ({ calendarData }: CalendarLegendProps) => {
  // "Failure" legend covers failure-class NC cell states (failure / override /
  // confirmed). Legend has no separate Non-Compliant item; aggregating under
  // Failure matches legacy calendar scanability. KPI still splits Failures vs NC.
  const hasFailure = calendarData.taskRows.some((row) =>
    row.cells.some(
      (cell) => cell.state === 'failure' || cell.state === 'override' || cell.state === 'confirmed',
    ),
  )

  const hasDateModified = calendarData.taskRows.some((row) =>
    row.cells.some((cell) => cell.meta.dateModified),
  )

  const items: LegendItemDef[] = [
    {
      key: 'failure',
      label: t('instruments.reports.legend.failure'),
      dotClass: QA_ALERT_CATEGORY_MAP.failures.dotColorClass,
      hasEvents: hasFailure,
    },
    {
      key: 'dateModification',
      label: t('instruments.reports.legend.dateModification'),
      dotClass: QA_ALERT_CATEGORY_MAP.dateChanges.dotColorClass,
      hasEvents: hasDateModified,
    },
    {
      key: 'serviceReport',
      label: t('instruments.reports.legend.serviceReport'),
      dotClass: QA_ALERT_CATEGORY_MAP.serviceReports.dotColorClass,
      hasEvents: calendarData.serviceReportRow.cells.some((cell) => cell.eventCount > 0),
    },
    {
      key: 'unscheduled',
      label: t('instruments.reports.legend.unscheduled'),
      // No qaAlertConfig entry — unscheduled is calendar-only.
      dotClass: 'text-purple-500',
      hasEvents: calendarData.unscheduledRow.cells.some((cell) => cell.eventCount > 0),
    },
    {
      key: 'statusChanges',
      label: t('instruments.reports.legend.statusChanges'),
      dotClass: QA_ALERT_CATEGORY_MAP.statusChanges.dotColorClass,
      hasEvents: calendarData.statusChangesRow.cells.some((cell) => cell.eventCount > 0),
    },
    {
      key: 'departmentMoved',
      label: t('instruments.reports.legend.departmentMoved'),
      dotClass: QA_ALERT_CATEGORY_MAP.departmentMoved.dotColorClass,
      hasEvents: calendarData.departmentMovedRow.cells.some((cell) => cell.eventCount > 0),
    },
  ]

  const visibleItems = items.filter((item) => item.hasEvents)
  if (visibleItems.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {visibleItems.map((item) => (
        <span key={item.key} className="flex items-center gap-1">
          <Circle className={`h-2.5 w-2.5 fill-current ${item.dotClass}`} />
          {item.label}
        </span>
      ))}
    </div>
  )
}

// ── Sign-off History block ───────────────────────────────────────────────────
type SignOffEntry = NonNullable<WebInstrumentCalendarView['signOffHistory']>[number]

interface SignOffHistoryBlockProps {
  signOffHistory: SignOffEntry[]
  loading: boolean
}

export const SignOffHistoryBlock = ({ signOffHistory, loading }: SignOffHistoryBlockProps) => {
  const isEmpty = signOffHistory.length === 0

  return (
    <div className={`space-y-2 ${loading ? 'pointer-events-none opacity-60' : ''}`}>
      <Separator />
      <h3 className="text-sm font-semibold">{t('instruments.reports.signOffHistory.title')}</h3>

      {isEmpty ? (
        <div className="rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {t('instruments.reports.signOffHistory.empty')}
        </div>
      ) : (
        <SignOffHistoryList entries={signOffHistory} />
      )}
    </div>
  )
}

interface SignOffHistoryListProps {
  entries: SignOffEntry[]
}

const SignOffHistoryList = ({ entries }: SignOffHistoryListProps) => {
  const groups: Map<number, SignOffEntry[]> = new Map()
  for (const entry of entries) {
    const key = entry.signoffChainSortOrder ?? 0
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(entry)
  }

  return (
    <div className="space-y-3">
      {[...groups.entries()].map(([order, groupEntries]) => (
        <div key={order} className="space-y-1">
          {groupEntries.map((entry, idx) => {
            const isSeparated = idx > 0
            return (
              <div key={`${entry.userId}-${entry.signedAt}`}>
                {isSeparated && <Separator className="my-1" />}
                <SignOffEntryRow entry={entry} />
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

interface SignOffEntryRowProps {
  entry: SignOffEntry
}

const SignOffEntryRow = ({ entry }: SignOffEntryRowProps) => {
  const { formatDateTime } = useSiteDateTime()
  const name = `${entry.userFirstName} ${entry.userLastName} (ID:${entry.userId})`
  const timestamp = entry.signedAt ? formatDateTime(entry.signedAt) : '—'

  return (
    <div
      className={`grid grid-cols-[1fr_auto_auto] items-center gap-x-6 text-xs ${entry.isUndo ? 'line-through opacity-60' : ''}`}
    >
      <span className="truncate">
        {name}
        {entry.isUndo && (
          <span className="ml-1 no-underline opacity-100">
            {t('instruments.reports.signOffHistory.undone')}
          </span>
        )}
      </span>
      <span className="text-muted-foreground whitespace-nowrap">{timestamp}</span>
      <span className="w-20 text-right">
        {entry.reportHref ? (
          <a href={entry.reportHref} className="text-primary underline-offset-4 hover:underline">
            {entry.reportPeriod}
          </a>
        ) : null}
      </span>
    </div>
  )
}

// ── SymbolLegend ─────────────────────────────────────────────────────────────
// Compact reference showing every calendar symbol and its meaning (AC #7).

interface LegendSymbolEntry {
  id: string
  state: CellState
  dateModified?: boolean
}

const LEGEND_ENTRIES: LegendSymbolEntry[] = [
  { id: 'success', state: 'success' },
  { id: 'success_date_changed', state: 'success', dateModified: true },
  { id: 'failure', state: 'failure' },
  { id: 'no_testing', state: 'no_testing' },
  { id: 'override', state: 'override' },
  { id: 'confirmed', state: 'confirmed' },
]

const LegendSymbol = ({ kind }: { kind: SymbolKind }) => {
  switch (kind) {
    case 'check':
      return <Check className="h-3 w-3 text-black" />
    case 'check_date_changed':
      return (
        <span className="relative mr-1.5 inline-flex text-black">
          <Check className="h-3 w-3" />
          <span
            className="absolute -right-1.5 -top-1 text-[9px] font-bold leading-none"
            aria-hidden
          >
            *
          </span>
        </span>
      )
    case 'x':
      return <X className="h-3 w-3 text-black" />
    case 'dash':
      return <Minus className="h-3 w-3 text-black" />
    case 'letter_o':
      return <span className="text-xs font-bold text-black leading-none">O</span>
    case 'x_badge':
      return (
        <span className="relative mr-1.5 inline-flex">
          <X className="h-3 w-3 text-black" />
          <span className="absolute -right-1.5 -top-1.5 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-black text-[7px] font-bold text-white leading-none">
            !
          </span>
        </span>
      )
    default:
      return null
  }
}

const SymbolLegend = () => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
    <span className="font-semibold">{t('instruments.reports.legend.symbolTitle')}:</span>
    {LEGEND_ENTRIES.map(({ id, state, dateModified }) => {
      const symbolDef = getCellSymbol(state, { dateModified })
      return (
        <span key={id} className="flex items-center gap-1">
          <span className="flex h-3 min-w-3 items-center justify-center">
            <LegendSymbol kind={symbolDef.kind} />
          </span>
          {symbolDef.label}
        </span>
      )
    })}
  </div>
)

// ── CalendarListView ──────────────────────────────────────────────────────────
// Flat list of completed task logs below the calendar grid.
// Controlled by the alert-only toggle: "Show full details" shows all entries,
// "Show alerts only" filters to alert rows only (failure, confirmed non-compliance, and
// date-modified logs — see calendarListAlerts.ts). Alerts-only is the default.

type CalendarGridProps = ComponentProps<typeof CalendarGrid>
type LogCellState = Parameters<NonNullable<CalendarGridProps['onLogCellClick']>>[0]['cellState']

interface CalendarListViewProps {
  calendarData: WebInstrumentCalendarView
  alertOnly: boolean
  /** Same handlers the calendar cells use, so the list opens the same result box. */
  onLogCellClick?: CalendarGridProps['onLogCellClick']
  onMissedCellClick?: CalendarGridProps['onMissedCellClick']
}

interface ListEntry {
  taskRow: TaskRow
  cell: TaskRow['cells'][number]
  taskName: string
  completingUser: string | null
  date: string
  state: string
}

// REBUILD-607: which reason a listed result carries. The backend sends one `reason` per
// cell; the label comes from the result type so each type reads as its own reason.
const DATE_MODIFIED_MARKER = '*'

const isDateModifiedSuccess = (cell: TaskRow['cells'][number]): boolean =>
  cell.state === 'success' && Boolean(cell.meta.dateModified)

const reasonLabelFor = (cell: TaskRow['cells'][number]): string | null => {
  if (!cell.meta.reason) return null
  if (cell.state === 'confirmed') return t('instruments.reports.listView.reasonLabel.nonCompliant')
  if (cell.state === 'override') return t('instruments.reports.listView.reasonLabel.override')
  if (isDateModifiedSuccess(cell))
    return t('instruments.reports.listView.reasonLabel.dateModification')
  return null
}

interface DataFieldLine {
  id: number
  label: string
  value: string | null
  hidden: boolean
}

// REBUILD-604: the entered result of each data field on the listed log, in field-definition
// order. Only fields the log has an entry for are listed, so a task with no data fields (or a
// log without entries) adds nothing under the task name. Confidential values arrive masked.
const dataFieldLinesFor = (taskRow: TaskRow, cell: TaskRow['cells'][number]): DataFieldLine[] => {
  const values = cell.meta.dataFieldValues ?? []
  if (values.length === 0) return []

  const valueByFieldId = new Map(values.map((entry) => [entry.instrumentTaskDataFieldId, entry]))
  return (taskRow.dataFields ?? []).flatMap((field) => {
    const entry = valueByFieldId.get(field.id)
    if (!entry) return []
    return [
      {
        id: field.id,
        label: field.label ?? '—',
        value: entry.value,
        hidden: entry.isConfidentialHidden,
      },
    ]
  })
}

const CalendarListView = ({
  calendarData,
  alertOnly,
  onLogCellClick,
  onMissedCellClick,
}: CalendarListViewProps) => {
  const { formatWallClockDisplay } = useSiteDateTime()
  const entries: ListEntry[] = []

  for (const taskRow of calendarData.taskRows) {
    for (const cell of taskRow.cells) {
      if (!isListedCalendarCell(cell)) continue
      if (alertOnly && !isAlertCalendarCell(cell)) continue
      entries.push({
        taskRow,
        cell,
        taskName: taskRow.taskName,
        completingUser: cell.meta.completedByName ?? null,
        date: cell.date,
        state: cell.state,
      })
    }
  }

  // REBUILD-605: the task name opens the same result box as clicking that result in the
  // calendar (identical routing: Log Details / day-occurrences). Rows with no openable
  // result (no log and no compliance row) stay plain text.
  const openEntry = ({ taskRow, cell }: ListEntry): (() => void) | null => {
    const common = {
      instrumentTaskId: taskRow.instrumentTaskId,
      taskName: taskRow.taskName,
      taskNotes: taskRow.taskNotes,
      date: cell.date,
      intervalTypeDescription: taskRow.intervalTypeDescription,
      dataFields: taskRow.dataFields ?? [],
    }

    if (isLogClickableCell(cell) && onLogCellClick) {
      return () =>
        onLogCellClick({
          ...common,
          taskLogId: cell.meta.instrumentTaskLogId!,
          cellState: cell.state as LogCellState,
        })
    }
    if (isMissedClickableCell(cell) && onMissedCellClick) {
      return () =>
        onMissedCellClick({
          ...common,
          requiredComplianceId: cell.meta.requiredComplianceId!,
          cellState: cell.state as 'failure' | 'override' | 'confirmed',
        })
    }
    return null
  }

  if (entries.length === 0) {
    // Match CalendarGrid: Failure / Status / Unscheduled / Service / Dept rows are
    // instrument-level (ignore task filter). Never contradict that grid content with a
    // list empty banner — including alert-only emptyAlerts (test: calendarEmptyCopy.test.mjs).
    if (hasNonTaskCalendarContent(calendarData)) {
      return null
    }

    // Grid owns period/filter empty when taskRows is empty — do not stack a second banner.
    // When taskRows exist but the list skips pending/day-off (filter matched; Grid still
    // shows the historical row), do not reuse Grid period-empty copy under a populated
    // calendar. emptyFilter stays Grid-only (zero taskRows). Only alert-only needs a
    // list-specific empty (Grid always renders full cells — alertOnly={false} above).
    if (calendarData.taskRows.length === 0 || !alertOnly) {
      return null
    }

    return (
      <div className="rounded border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        {t('instruments.reports.listView.emptyAlerts')}
      </div>
    )
  }

  return (
    <div className="space-y-1 rounded border">
      {/* REBUILD-607: below `sm` the list rows stack (task, user, date) instead of sharing one
          grid row, because the no-wrap user name and date-modified time (`MM/DD/YYYY, HH:mm TZ*`)
          would otherwise leave the Task column 0px wide at 375px. The column header only makes
          sense for the side-by-side layout. */}
      <div className="hidden grid-cols-[1fr_auto_auto] gap-x-4 border-b px-3 py-1.5 text-xs font-semibold text-muted-foreground sm:grid">
        <span>{t('instruments.reports.listView.colTask')}</span>
        <span>{t('instruments.reports.listView.colUser')}</span>
        <span>{t('instruments.reports.listView.colDate')}</span>
      </div>
      <div className="max-h-64 overflow-y-auto">
        {entries.map((entry, entryIndex) => {
          const open = openEntry(entry)
          // Snapshot payloads written before REBUILD-606 carry no commentCount.
          const commentCount = entry.cell.meta.commentCount ?? 0
          const reasonLabel = reasonLabelFor(entry.cell)
          const dataFieldLines = dataFieldLinesFor(entry.taskRow, entry.cell)
          return (
            // REBUILD-605: the whole row opens the result box, like a Maintenance Logs history
            // row. The task-name button stays the keyboard target; its click bubbles to the row.
            <div
              key={`${entry.taskName}-${entry.date}-${entryIndex}`}
              className={cn(
                'grid grid-cols-1 gap-y-0.5 px-3 py-1 text-xs odd:bg-muted/20 sm:grid-cols-[1fr_auto_auto] sm:gap-x-4',
                open && 'cursor-pointer transition-colors hover:bg-muted/50',
              )}
              onClick={open ?? undefined}
            >
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-1.5">
                  {open ? (
                    <button
                      type="button"
                      className="cursor-pointer truncate text-left font-medium text-primary underline-offset-4 hover:underline"
                      title={entry.taskName}
                    >
                      {entry.taskName}
                    </button>
                  ) : (
                    <span className="truncate font-medium" title={entry.taskName}>
                      {entry.taskName}
                    </span>
                  )}
                  {entry.taskRow.taskInactive && (
                    <span className="shrink-0 font-normal text-muted-foreground">
                      {t('instruments.reports.cell.inactiveTaskSuffix')}
                    </span>
                  )}
                  <CommentCountIndicator count={commentCount} />
                </div>
                {reasonLabel && (
                  <p className="mt-0.5 whitespace-pre-line break-words text-muted-foreground">
                    <span className="font-medium">{reasonLabel}:</span> {entry.cell.meta.reason}
                  </p>
                )}
                {dataFieldLines.length > 0 && (
                  <ul
                    className="mt-0.5 space-y-0.5 text-muted-foreground"
                    data-testid="list-data-field-values"
                  >
                    {dataFieldLines.map((line) => (
                      <li key={line.id} className="break-words">
                        <span className="font-medium">{line.label}:</span>{' '}
                        {line.hidden ? (
                          <span title={t('common.confidentialData')}>{CONFIDENTIAL_MASK}</span>
                        ) : (
                          (line.value ?? '—')
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <span className="min-w-0 break-words text-muted-foreground sm:whitespace-nowrap">
                {entry.completingUser ?? '—'}
              </span>
              {isDateModifiedSuccess(entry.cell) ? (
                // REBUILD-607 (legacy calendarView.template.html:410): a date-modified result shows
                // its completion time in italics followed by "*".
                <span
                  className="min-w-0 break-words text-muted-foreground italic sm:whitespace-nowrap"
                  data-testid="list-date-modified-time"
                >
                  {(entry.cell.meta.completedAtLocal
                    ? formatWallClockDisplay(entry.cell.meta.completedAtLocal)
                    : entry.date) + DATE_MODIFIED_MARKER}
                </span>
              ) : (
                <span className="min-w-0 break-words text-muted-foreground sm:whitespace-nowrap">
                  {entry.date}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default InstrumentCalendarViewPanel
